import { useState } from 'react';
import { Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Typography, App as AntApp } from 'antd';
import { EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCreateProject, useUpdateProject, useProjectsList, type Project, type ProjectStatus } from '../api/projects';
import { RoleGate, ADMIN_ROLES } from '@/shared/auth/role';
import { asApiError } from '@/shared/lib/api';

const STATUS_COLOR: Record<ProjectStatus, string> = {
  planning: 'default',
  construction: 'gold',
  selling: 'green',
  sold_out: 'volcano',
  delivered: 'blue',
  archived: 'default',
};

export function ProjectsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);

  const { data, isLoading } = useProjectsList({ search: search || undefined });
  const create = useCreateProject();
  const update = useUpdateProject();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  function handleEdit(p: Project) {
    setEditProject(p);
    editForm.setFieldsValue({ name: p.name, address: p.address, city: p.city, description: p.description, status: p.status });
  }

  async function onEdit() {
    if (!editProject) return;
    try {
      const values = await editForm.validateFields();
      await update.mutateAsync({ id: editProject.id, input: values });
      void message.success(t('projects.updated'));
      setEditProject(null);
      editForm.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  async function onCreate() {
    try {
      const values = await form.validateFields();
      const project = await create.mutateAsync(values);
      void message.success(t('projects.created'));
      setCreateOpen(false);
      form.resetFields();
      navigate(`/projects/${project.id}`);
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      const apiErr = asApiError(err);
      void message.error(apiErr.message);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('projects.title')}
        </Typography.Title>
        <RoleGate roles={ADMIN_ROLES}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('projects.new')}
          </Button>
        </RoleGate>
      </div>

      <Card bodyStyle={{ padding: 16 }}>
        <Input
          placeholder={t('projects.searchPlaceholder')}
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 360 }}
        />
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table<Project>
          rowKey="id"
          loading={isLoading}
          dataSource={data?.data ?? []}
          pagination={false}
          onRow={(record) => ({ onClick: () => navigate(`/projects/${record.id}`), style: { cursor: 'pointer' } })}
          columns={[
            { title: t('projects.cols.code'), dataIndex: 'code', width: 120, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
            { title: t('projects.cols.name'), dataIndex: 'name' },
            { title: t('projects.cols.address'), dataIndex: 'address', ellipsis: true },
            {
              title: t('projects.cols.status'),
              dataIndex: 'status',
              width: 130,
              render: (s: ProjectStatus) => (
                <Tag color={STATUS_COLOR[s]}>{t(`projects.status.${s}`)}</Tag>
              ),
            },
            {
              title: t('projects.cols.apartments'),
              width: 120,
              render: (_: unknown, r: Project) => r._count?.apartments ?? 0,
            },
            {
              title: '',
              width: 60,
              render: (_: unknown, r: Project) => (
                <RoleGate roles={ADMIN_ROLES}>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleEdit(r); }}
                  />
                </RoleGate>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={t('projects.newTitle')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        confirmLoading={create.isPending}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ city: 'Tashkent', status: 'planning' }}>
          <Space.Compact style={{ display: 'flex', width: '100%' }}>
            <Form.Item
              name="code"
              label={t('projects.code')}
              rules={[{ required: true, pattern: /^[A-Z0-9-]+$/i, message: t('projects.codeHelp') }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="PG-001" />
            </Form.Item>
            <Form.Item name="status" label={t('projects.status_')} style={{ flex: 1, marginLeft: 8 }}>
              <Select>
                {(['planning', 'construction', 'selling', 'sold_out', 'delivered', 'archived'] as ProjectStatus[]).map(
                  (s) => (
                    <Select.Option key={s} value={s}>
                      {t(`projects.status.${s}`)}
                    </Select.Option>
                  ),
                )}
              </Select>
            </Form.Item>
          </Space.Compact>

          <Form.Item name="name" label={t('projects.name')} rules={[{ required: true }]}>
            <Input placeholder="Pop Garden A1" />
          </Form.Item>
          <Form.Item name="address" label={t('projects.address')} rules={[{ required: true }]}>
            <Input placeholder="Yunusobod district, Tashkent" />
          </Form.Item>
          <Form.Item name="city" label={t('projects.city')}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('projects.description')}>
            <Input.TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t('projects.editTitle')}
        open={!!editProject}
        onCancel={() => { setEditProject(null); editForm.resetFields(); }}
        onOk={onEdit}
        confirmLoading={update.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" requiredMark={false}>
          <Form.Item name="status" label={t('projects.status_')}>
            <Select>
              {(['planning', 'construction', 'selling', 'sold_out', 'delivered', 'archived'] as ProjectStatus[]).map(
                (s) => <Select.Option key={s} value={s}>{t(`projects.status.${s}`)}</Select.Option>
              )}
            </Select>
          </Form.Item>
          <Form.Item name="name" label={t('projects.name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label={t('projects.address')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="city" label={t('projects.city')}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('projects.description')}>
            <Input.TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
